import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { Input } from "@/components/ui/Input";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

const CELL_INPUT = "h-8 w-full min-w-[4rem] text-xs";

export interface AdminLocationRow {
  id: number;
  megaventory_id: number | null;
  name: string | null;
  abbreviation: string | null;
  ship_address_line1: string | null;
  ship_address_line2: string | null;
  ship_address_line3: string | null;
  ship_city: string | null;
  ship_state: string | null;
  ship_postal_code: string | null;
  ship_country_code: string | null;
  phone: string | null;
  default_shipping_method: string | null;
  erp_id: string | null;
  ship_recipient: string | null;
  ship_attention: string | null;
  mrp_site_code: string | null;
  mrp_planning_sort_order: number;
  mrp_planning_active: boolean;
  include_in_forecast: boolean;
  enabled: boolean;
  erp_updated: string | null;
  sb_updated: string;
}

function cloneRow(row: AdminLocationRow): AdminLocationRow {
  return { ...row };
}

export function Locations() {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useHubModuleWrite("admin");
  const adminReadOnlyTitle = "Your role has read-only access to the Admin hub module.";
  const locationsQuery = useQuery<{ locations: AdminLocationRow[] }, ApiError>({
    queryKey: ["user-admin", "locations"],
    queryFn: () => apiFetch<{ locations: AdminLocationRow[] }>("/api/user-admin/locations"),
  });

  const [draftById, setDraftById] = useState<Record<number, AdminLocationRow>>({});

  useEffect(() => {
    const list = locationsQuery.data?.locations;
    if (!list) return;
    setDraftById(
      Object.fromEntries(list.map((row) => [row.id, cloneRow(row)])) as Record<number, AdminLocationRow>,
    );
  }, [locationsQuery.data]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["user-admin", "locations"] });
    void queryClient.invalidateQueries({ queryKey: ["mrp", "locations"] });
  };

  const saveMutation = useMutation({
    mutationFn: (input: { id: number; body: Record<string, unknown> }) =>
      apiFetch<{ location: AdminLocationRow }>(`/api/user-admin/locations/${input.id}`, {
        method: "PATCH",
        body: input.body,
      }),
    onSuccess: () => {
      invalidate();
    },
  });

  const serverRows = locationsQuery.data?.locations ?? [];

  function updateDraft(id: number, patch: Partial<AdminLocationRow>): void {
    setDraftById((current) => {
      const list = locationsQuery.data?.locations ?? [];
      const base = current[id] ?? list.find((locationRow) => locationRow.id === id);
      if (!base) return current;
      return { ...current, [id]: { ...base, ...patch } };
    });
  }

  function buildPatch(draft: AdminLocationRow): Record<string, unknown> {
    let megaventory_id: number | null = draft.megaventory_id;
    if (draft.megaventory_id != null && !Number.isFinite(draft.megaventory_id)) {
      megaventory_id = null;
    }
    return {
      name: draft.name,
      abbreviation: draft.abbreviation,
      megaventory_id,
      ship_address_line1: draft.ship_address_line1,
      ship_address_line2: draft.ship_address_line2,
      ship_address_line3: draft.ship_address_line3,
      ship_city: draft.ship_city,
      ship_state: draft.ship_state,
      ship_postal_code: draft.ship_postal_code,
      ship_country_code: draft.ship_country_code,
      phone: draft.phone,
      default_shipping_method: draft.default_shipping_method,
      erp_id: draft.erp_id,
      ship_recipient: draft.ship_recipient,
      ship_attention: draft.ship_attention,
      mrp_site_code: draft.mrp_site_code === "" ? null : draft.mrp_site_code,
      mrp_planning_sort_order: draft.mrp_planning_sort_order,
      mrp_planning_active: draft.mrp_planning_active,
      include_in_forecast: draft.include_in_forecast,
      enabled: draft.enabled,
    };
  }

  return (
    <div className="space-y-4">
      <header>
        <PageTitleWithHelp hoverHint="Edit public.location (warehouses, contract manufacturers, MRP planning sites). Changes apply immediately for MRP and forecast. Use a lowercase mrp_site_code (for example sgs) so it matches inventory and build-plan keys.">
          Locations
        </PageTitleWithHelp>
      </header>

      {locationsQuery.isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : locationsQuery.isError ? (
        <div className="text-sm text-rose-700">
          <p>{locationsQuery.error.message}</p>
          {locationsQuery.error instanceof ApiError && locationsQuery.error.status === 404 ? (
            <p className="mt-2 font-normal text-slate-600">
              This usually means the server answering <code className="rounded bg-slate-100 px-1 text-xs">/api</code>{" "}
              does not expose{" "}
              <code className="rounded bg-slate-100 px-1 text-xs">GET /api/user-admin/locations</code> (for example
              an older local API process or production before that route shipped).
            </p>
          ) : null}
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">All locations</CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle} layout="block">
            <fieldset
              disabled={!hubWritesAllowed}
              className="border-0 p-0 min-w-0"
            >
            <div className="overflow-x-auto">
              <table className="min-w-[2000px] w-full border-collapse border border-slate-200 text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                    <th className="sticky left-0 z-20 border-r border-slate-200 bg-slate-50 px-2 py-2">Id</th>
                    <th className="min-w-[7rem] px-2 py-2">Name</th>
                    <th className="min-w-[5rem] px-2 py-2">Abbreviation</th>
                    <th className="min-w-[5rem] px-2 py-2 whitespace-normal leading-tight">
                      Megaventory location id
                    </th>
                    <th className="min-w-[5rem] px-2 py-2 whitespace-normal leading-tight">MRP site code</th>
                    <th className="min-w-[5rem] px-2 py-2 whitespace-normal leading-tight">Planning sort order</th>
                    <th className="min-w-[6rem] px-2 py-2">ERP id</th>
                    <th className="min-w-[4rem] px-2 py-2 text-center whitespace-normal leading-tight">
                      Planning active
                    </th>
                    <th className="min-w-[4rem] px-2 py-2 text-center whitespace-normal leading-tight">
                      In forecast
                    </th>
                    <th className="min-w-[4rem] px-2 py-2 text-center whitespace-normal leading-tight">
                      Enabled
                    </th>
                    <th className="min-w-[6rem] px-2 py-2 whitespace-normal leading-tight">Address line 1</th>
                    <th className="min-w-[5rem] px-2 py-2 whitespace-normal leading-tight">Address line 2</th>
                    <th className="min-w-[5rem] px-2 py-2 whitespace-normal leading-tight">Address line 3</th>
                    <th className="min-w-[5rem] px-2 py-2">City</th>
                    <th className="min-w-[3.5rem] px-2 py-2">State</th>
                    <th className="min-w-[4rem] px-2 py-2 whitespace-normal leading-tight">Postal code</th>
                    <th className="min-w-[3rem] px-2 py-2 whitespace-normal leading-tight">Country</th>
                    <th className="min-w-[5rem] px-2 py-2">Phone</th>
                    <th className="min-w-[5rem] px-2 py-2 whitespace-normal leading-tight">Shipping method</th>
                    <th className="min-w-[5rem] px-2 py-2">Recipient</th>
                    <th className="min-w-[5rem] px-2 py-2">Attention</th>
                    <th className="sticky right-0 z-20 border-l border-slate-200 bg-slate-50 px-2 py-2 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {serverRows.map((row) => {
                    const draft = draftById[row.id] ?? row;
                    const saving = saveMutation.isPending && saveMutation.variables?.id === row.id;
                    const disabledRow = draft.enabled === false;
                    return (
                      <tr
                        key={row.id}
                        className={disabledRow ? "align-top bg-slate-50/80 text-slate-500" : "align-top"}
                      >
                        <td
                          className={
                            disabledRow
                              ? "sticky left-0 z-10 border-r border-slate-100 bg-slate-50/90 px-2 py-1.5 font-mono text-xs"
                              : "sticky left-0 z-10 border-r border-slate-100 bg-white px-2 py-1.5 font-mono text-xs text-slate-700"
                          }
                        >
                          {row.id}
                          {disabledRow ? (
                            <span className="ml-1 rounded bg-slate-200 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                              Disabled
                            </span>
                          ) : null}
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={draft.name ?? ""}
                            onChange={(event) => updateDraft(row.id, { name: event.target.value || null })}
                            className={CELL_INPUT}
                            aria-label={`Name for location ${row.id}`}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={draft.abbreviation ?? ""}
                            onChange={(event) =>
                              updateDraft(row.id, { abbreviation: event.target.value || null })
                            }
                            className={CELL_INPUT}
                            aria-label={`Abbreviation for location ${row.id}`}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            type="number"
                            value={draft.megaventory_id ?? ""}
                            onChange={(event) => {
                              const raw = event.target.value;
                              if (raw === "") {
                                updateDraft(row.id, { megaventory_id: null });
                                return;
                              }
                              const n = Number.parseInt(raw, 10);
                              updateDraft(row.id, { megaventory_id: Number.isFinite(n) ? n : null });
                            }}
                            className={CELL_INPUT}
                            aria-label={`Megaventory location id for location ${row.id}`}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={draft.mrp_site_code ?? ""}
                            onChange={(event) =>
                              updateDraft(row.id, {
                                mrp_site_code: event.target.value.trim().toLowerCase() || null,
                              })
                            }
                            className={`${CELL_INPUT} font-mono`}
                            aria-label={`MRP site code for location ${row.id}`}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            type="number"
                            value={draft.mrp_planning_sort_order}
                            onChange={(event) => {
                              const n = Number.parseInt(event.target.value, 10);
                              updateDraft(row.id, {
                                mrp_planning_sort_order: Number.isFinite(n) ? n : 0,
                              });
                            }}
                            className={CELL_INPUT}
                            aria-label={`MRP sort order for location ${row.id}`}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={draft.erp_id ?? ""}
                            onChange={(event) => updateDraft(row.id, { erp_id: event.target.value || null })}
                            className={`${CELL_INPUT} font-mono`}
                            aria-label={`ERP id for location ${row.id}`}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <input
                            type="checkbox"
                            className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                            checked={draft.mrp_planning_active}
                            onChange={(event) =>
                              updateDraft(row.id, { mrp_planning_active: event.target.checked })
                            }
                            aria-label={`MRP planning active for location ${row.id}`}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <input
                            type="checkbox"
                            className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                            checked={draft.include_in_forecast}
                            onChange={(event) =>
                              updateDraft(row.id, { include_in_forecast: event.target.checked })
                            }
                            aria-label={`Include in forecast for location ${row.id}`}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <input
                            type="checkbox"
                            className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                            checked={draft.enabled}
                            onChange={(event) => updateDraft(row.id, { enabled: event.target.checked })}
                            aria-label={`Enabled for location ${row.id}`}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={draft.ship_address_line1 ?? ""}
                            onChange={(event) =>
                              updateDraft(row.id, { ship_address_line1: event.target.value || null })
                            }
                            className={CELL_INPUT}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={draft.ship_address_line2 ?? ""}
                            onChange={(event) =>
                              updateDraft(row.id, { ship_address_line2: event.target.value || null })
                            }
                            className={CELL_INPUT}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={draft.ship_address_line3 ?? ""}
                            onChange={(event) =>
                              updateDraft(row.id, { ship_address_line3: event.target.value || null })
                            }
                            className={CELL_INPUT}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={draft.ship_city ?? ""}
                            onChange={(event) => updateDraft(row.id, { ship_city: event.target.value || null })}
                            className={CELL_INPUT}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={draft.ship_state ?? ""}
                            onChange={(event) => updateDraft(row.id, { ship_state: event.target.value || null })}
                            className={CELL_INPUT}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={draft.ship_postal_code ?? ""}
                            onChange={(event) =>
                              updateDraft(row.id, { ship_postal_code: event.target.value || null })
                            }
                            className={CELL_INPUT}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={draft.ship_country_code ?? ""}
                            onChange={(event) =>
                              updateDraft(row.id, {
                                ship_country_code:
                                  event.target.value.trim().slice(0, 2).toUpperCase() || null,
                              })
                            }
                            maxLength={2}
                            className={`${CELL_INPUT} uppercase`}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={draft.phone ?? ""}
                            onChange={(event) => updateDraft(row.id, { phone: event.target.value || null })}
                            className={CELL_INPUT}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={draft.default_shipping_method ?? ""}
                            onChange={(event) =>
                              updateDraft(row.id, { default_shipping_method: event.target.value || null })
                            }
                            className={CELL_INPUT}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={draft.ship_recipient ?? ""}
                            onChange={(event) =>
                              updateDraft(row.id, { ship_recipient: event.target.value || null })
                            }
                            className={CELL_INPUT}
                          />
                        </td>
                        <td className="px-2 py-1.5">
                          <Input
                            value={draft.ship_attention ?? ""}
                            onChange={(event) =>
                              updateDraft(row.id, { ship_attention: event.target.value || null })
                            }
                            className={CELL_INPUT}
                          />
                        </td>
                        <td
                          className={
                            disabledRow
                              ? "sticky right-0 z-10 border-l border-slate-100 bg-slate-50/90 px-2 py-1.5"
                              : "sticky right-0 z-10 border-l border-slate-100 bg-white px-2 py-1.5"
                          }
                        >
                          <div className="flex flex-col gap-1">
                            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={adminReadOnlyTitle} layout="block">
                            <Button
                              type="button"
                              size="sm"
                              className="w-full"
                              isLoading={saving}
                              disabled={!hubWritesAllowed}
                              onClick={() => saveMutation.mutate({ id: row.id, body: buildPatch(draft) })}
                            >
                              Save
                            </Button>
                            </ReadOnlyHoverLayer>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              className="w-full"
                              onClick={() => {
                                const fresh = serverRows.find((locationRow) => locationRow.id === row.id);
                                if (fresh) {
                                  setDraftById((current) => ({
                                    ...current,
                                    [row.id]: cloneRow(fresh),
                                  }));
                                }
                              }}
                            >
                              Reset
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </fieldset>
            </ReadOnlyHoverLayer>
            {saveMutation.isError ? (
              <p className="border-t border-slate-100 px-4 py-3 text-sm text-rose-700">
                {(saveMutation.error as ApiError).message}
              </p>
            ) : null}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
